import {
  DoublePurchaseExtension,
  DoublePurchaseModule,
  RevShareExtension,
  StudioOfferingApiExtension,
  SwitchProductModule
} from '@wix/package-picker-sdk';
import {
  getSearchParams,
  P3Extension,
  PackagePickerSteps,
  packagePickerTranslationsExtension,
  PREMIUM_PLAN_PRODUCT_TYPE_ID,
  wixServerData
} from '@wix/package-picker-shared';
import {
  PackagePickerStoreExtension,
  RouteGuard,
  RouterExtension,
  StudioBiEventsExtension,
  StudioErrorsExtension,
  StudioExperimentsExtension,
  StudioModalsExtension
} from '@wix/package-picker-shared-studio';
import { ApisClientProvider } from '@wix/premium-fed-sdk/@apis-infra-react';
import { createPremiumModule } from '@wix/premium-fed-sdk/@premium-module';
import { PremiumPurchasePlatformContext } from '@wix/premium-purchase-platform/@sdk';
import React, { PropsWithChildren } from 'react';
import { Application } from '../components/Application';
import { CheckoutPage } from '../pages/CheckoutPage/CheckoutPage';
import { SelectCyclePage } from '../pages/SelectCyclePage/SelectCyclePage';
import { SelectPlanPage } from '../pages/SelectPlanPage/SelectPlanPage';

const routes = new Map<PackagePickerSteps, React.FC>([
  [
    PackagePickerSteps.SELECT_PLAN,
    () => (
      <RouteGuard
        step={PackagePickerSteps.SELECT_PLAN}
        Element={SelectPlanPage}
      />
    ),
  ],
  [
    PackagePickerSteps.SELECT_CYCLE,
    () => (
      <RouteGuard
        step={PackagePickerSteps.SELECT_CYCLE}
        Element={SelectCyclePage}
      />
    ),
  ],
  [
    PackagePickerSteps.CHECKOUT,
    () => (
      <RouteGuard step={PackagePickerSteps.CHECKOUT} Element={CheckoutPage} />
    ),
  ],
  [
    PackagePickerSteps.ANY,
    () => (
      <RouteGuard
        step={PackagePickerSteps.SELECT_PLAN}
        Element={SelectPlanPage}
      />
    ),
  ],
]);

const p3Context: PremiumPurchasePlatformContext = {
  productTypeId: PREMIUM_PLAN_PRODUCT_TYPE_ID,
  purchaseContext: {
    purchaseType: 'studio',
    overrideSubscriptionId: getSearchParams().overrideSubscriptionId,
  },
};

export const StudioPackagePickerModule = createPremiumModule(
  'studio-package-picker-module',
  [
    StudioBiEventsExtension,
    packagePickerTranslationsExtension,
    StudioExperimentsExtension,
    StudioErrorsExtension({ notificationButtonType: 'button' }),
    PackagePickerStoreExtension,
    P3Extension(p3Context),
    SwitchProductModule.SDK.SwitchExtension,
    DoublePurchaseExtension,
    RouterExtension(routes),
    StudioOfferingApiExtension,
    RevShareExtension,
    StudioModalsExtension,
  ],
  (module) => {
    return {
      SDK: { Application },
      Provider: ({ children }: PropsWithChildren<{}>) => {
        const { siteGuid, isDowngradeEnabled } = getSearchParams();
        const { locale } = wixServerData.get();
        SwitchProductModule.SDK.switchContext.set('p3Context', p3Context);
        module.SDK.studioOfferingContext.set('p3Context', p3Context);
        module.SDK.revShareContext.set('p3Context', p3Context);

        return (
          <DoublePurchaseModule.Provider
            context={{
              siteGuid,
              productTypeId: PREMIUM_PLAN_PRODUCT_TYPE_ID,
            }}
          >
            <SwitchProductModule.Provider
              context={{
                siteGuid,
                isDowngradeEnabled,
                // ask Botzer why there is no p3context here?
                locale
              }}
            >
              <ApisClientProvider useErrorBoundary={true}>
                <module.SDK.Modals />
                {children}
              </ApisClientProvider>
            </SwitchProductModule.Provider>
          </DoublePurchaseModule.Provider>
        );
      },
    };
  },
);
